import { Color } from "@repo/app-types/constants/colors";
import {
  getPassScoreColor,
  getPassScoreLabel,
} from "@repo/app-types/constants/passScore";
import type {
  RecordingMetadataSpeaker,
  RecordingRawTranscriptFragment,
} from "@repo/app-types/entities/recording";
import {
  transcriptSearchMatchSchema,
  type AccountGradingRecording,
  type GradingId,
  type TranscriptSearchMatch,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { parseGradingValue } from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { buildPersonName } from "@repo/app-types/helpers/utilities";
import { getPillColor, Pill } from "@repo/ui/Pills/pill";
import { Size } from "@repo/ui/helpers/helpers";
import { fuzzySearch } from "levenshtein-search";
import { useMemo, type ReactNode } from "react";
import { z } from "zod";
import {
  AssessmentScore,
  LabeledAssessmentScore,
} from "../insights/assessmentScore.js";

export function useSortedRecordings(
  recordings: readonly AccountGradingRecording[] | undefined,
) {
  return useMemo(() => {
    if (!recordings) {
      return [];
    }

    return recordings.toSorted((r1, r2) => {
      if (r1.recorded_at === r2.recorded_at) {
        return 0;
      } else if (r1.recorded_at > r2.recorded_at) {
        return 1;
      } else {
        return -1;
      }
    });
  }, [recordings]);
}

/** Opens a grading on Transcript without redirecting away from the modal's history entry. */
export function buildGradingModalPath(gradingId: GradingId): string {
  return `${encodeURIComponent(gradingId)}/transcript`;
}

export interface TranscriptSearchNavState {
  query: string;
  matches: readonly TranscriptSearchMatch[];
}

const transcriptSearchNavStateSchema = z.object({
  transcriptSearch: z.object({
    query: z.string(),
    matches: z.array(transcriptSearchMatchSchema),
  }),
});

export function parseTranscriptSearchNavState(
  state: unknown,
): TranscriptSearchNavState | undefined {
  const parsed = transcriptSearchNavStateSchema.safeParse(state);
  return parsed.success ? parsed.data.transcriptSearch : undefined;
}

/**
 * The `modalNavigation.push` extra-state payload that
 * {@link parseTranscriptSearchNavState} reads back on the transcript page.
 */
export function buildTranscriptSearchNavState(
  query: string,
  matches: readonly TranscriptSearchMatch[],
): { transcriptSearch: TranscriptSearchNavState } {
  return { transcriptSearch: { query, matches } };
}

export function renderSpeakerName(
  speakers: readonly RecordingMetadataSpeaker[] | null | undefined,
  speakerId: string | undefined,
): ReactNode {
  const speaker =
    speakerId !== undefined
      ? speakers?.find((s) => s.id === speakerId)
      : undefined;

  return buildPersonName(speaker) || speaker?.name || "Unknown speaker";
}

export type RecordingFragment = RecordingRawTranscriptFragment;

export interface RecordingCitation {
  start_time: number;
  text: string;
}

interface RecordingCitationWithFragment {
  speaker_id: string | undefined;
  start_time: number;
  text_before: string | undefined;
  text: string;
  text_after: string | undefined;
}

export function findFragmentForCitation(
  fragments: readonly RecordingFragment[],
  citation: RecordingCitation,
): RecordingCitationWithFragment {
  let bestResult: RecordingCitationWithFragment = {
    speaker_id: undefined,
    start_time: citation.start_time,
    text_before: undefined,
    text: citation.text,
    text_after: undefined,
  };
  let bestDist = Infinity;

  // Find the nearest three fragments around the citation start time, since the time might be slightly off due to rounding
  const aroundFragmentIndex = findFragmentIndexAtTime(
    fragments,
    citation.start_time,
  );
  const firstFragmentIndex = Math.max(0, aroundFragmentIndex - 1);
  const lastFragmentIndex = Math.min(
    aroundFragmentIndex + 1,
    fragments.length - 1,
  );

  // Fuzzy match with an edit distance of 10% of the citation text length, within reasonable runtime bounds
  const maxDist = Math.max(
    1,
    Math.min(Math.round(0.1 * citation.text.length), 16),
  );

  // Iterate through those fragments
  for (
    let fragmentIndex = firstFragmentIndex;
    fragmentIndex <= lastFragmentIndex;
    fragmentIndex++
  ) {
    // There's nothing better to find
    if (bestDist === 0) {
      break;
    }

    const fragment = fragments.at(fragmentIndex);
    if (!fragment) {
      continue;
    }

    // Iterate through matches
    for (const match of fuzzySearch(
      citation.text.toLowerCase(),
      fragment.text_transcript.toLowerCase(),
      maxDist,
    )) {
      // Is this a better match than what we already have?
      const matchLength = match.end - match.start;
      if (
        match.dist < bestDist ||
        (match.dist === bestDist && matchLength > bestResult.text.length)
      ) {
        bestResult = {
          speaker_id: fragment.speaker_id,
          start_time: citation.start_time,
          text_before: fragment.text_transcript.slice(0, match.start),
          text: fragment.text_transcript.slice(match.start, match.end),
          text_after: fragment.text_transcript.slice(match.end),
        };
        bestDist = match.dist;

        if (bestDist === 0) {
          break;
        }
      }
    }
  }

  return bestResult;
}

export function findFragmentIndexAtTime(
  fragments: readonly RecordingFragment[],
  time: number,
): number {
  let left = 0;
  let right = fragments.length - 1;

  // Use a modified binary search to find the nearest fragment
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const fragment = fragments[mid];
    if (fragment === undefined) {
      break;
    }

    if (fragment.start_time <= time && time < fragment.end_time) {
      return mid;
    } else if (fragment.end_time <= time) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return left;
}

/** Find the transcript fragment represented by a key moment. */
export function findFragmentIndexForKeyMoment(
  fragments: readonly RecordingFragment[],
  time: number,
): number | undefined {
  const displayedSecond = Math.floor(time / 1000);
  let matchingStartIndex: number | undefined;
  let matchingStartDistance = Infinity;
  let overlappingIndex: number | undefined;
  let latestOverlappingStart = -Infinity;
  let nextIndex: number | undefined;
  let earliestNextStart = Infinity;

  for (const [index, fragment] of fragments.entries()) {
    // Both timestamps are displayed to whole seconds, even when their source
    // values differ by milliseconds.
    if (
      Math.floor(fragment.start_time / 1000) === displayedSecond &&
      fragment.end_time > time
    ) {
      const distance = Math.abs(fragment.start_time - time);
      if (distance < matchingStartDistance) {
        matchingStartIndex = index;
        matchingStartDistance = distance;
      }
    }

    if (
      fragment.start_time <= time &&
      time < fragment.end_time &&
      fragment.start_time > latestOverlappingStart
    ) {
      overlappingIndex = index;
      latestOverlappingStart = fragment.start_time;
    }

    if (fragment.start_time > time && fragment.start_time < earliestNextStart) {
      nextIndex = index;
      earliestNextStart = fragment.start_time;
    }
  }

  return matchingStartIndex ?? overlappingIndex ?? nextIndex;
}

// TODO: Implement separate functions for this.
export function renderGradingValue(
  valueType: "yes_no" | "1_5_score",
  value: string | undefined,
  options: { plaintext: true; showLabel?: boolean },
): string;
export function renderGradingValue(
  valueType: "yes_no" | "1_5_score",
  value: string | undefined,
  options?: { plaintext?: false; showLabel?: boolean },
): ReactNode;
export function renderGradingValue(
  valueType: "yes_no" | "1_5_score",
  value: string | undefined,
  options?: { plaintext?: boolean; showLabel?: boolean },
): string | ReactNode {
  const gradingValue = parseGradingValue(valueType, value);

  if (gradingValue === undefined) {
    return options?.plaintext ? (
      "--"
    ) : (
      <span className="text-muted-foreground">--</span>
    );
  }

  if (gradingValue.value === null) {
    return options?.plaintext ? (
      "N/A"
    ) : (
      <Pill size={Size.SMALL} color={Color.Gray} label="N/A" />
    );
  }

  if (gradingValue.type === "yes_no") {
    const label = getPassScoreLabel(gradingValue.value);
    return options?.plaintext ? (
      label
    ) : (
      <Pill
        size={Size.SMALL}
        color={getPillColor(getPassScoreColor(gradingValue.value))}
        label={label}
      />
    );
  }

  if (gradingValue.type === "1_5_score") {
    if (options?.plaintext) {
      return `${gradingValue.value}/5`;
    }

    return (options?.showLabel ?? true) ? (
      <LabeledAssessmentScore size={Size.SMALL} score={gradingValue.value} />
    ) : (
      <AssessmentScore size={Size.SMALL} score={gradingValue.value} />
    );
  }

  return options?.plaintext ? (
    "--"
  ) : (
    <span className="text-muted-foreground">--</span>
  );
}
